import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, tap } from 'rxjs';
import { DrupalRESTService } from './drupal-rest.service';
import { EntityRESTService } from './entity-rest.service';
import { AuthService } from './auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class PackagesService {
  private packagesSubject = new BehaviorSubject<any[]>([]);
  packages$ = this.packagesSubject.asObservable();

  constructor(
    private drupalRESTService: DrupalRESTService,
    private entityRESTService: EntityRESTService,
    private authService: AuthService,
    private snackBar: MatSnackBar
  ) {
    // Only fetch on construction if a user is already authenticated. The service
    // is instantiated during app bootstrap (BaseComponent -> menu), which happens
    // before login resolves and also on the logged-out login page; fetching there
    // hits /api/v1/packages as an anonymous user and 403s. The login and app-init
    // flows call fetchPackages() again once authentication is established.
    if (this.authService.is_user_authenticated()) {
      this.fetchPackages();
    }
  }

  fetchPackages() {
    this.drupalRESTService.httpGET('/api/v1/packages').subscribe(
      data => {
        const packages = data?.['results'] || [];
        this.packagesSubject.next(packages);
      },
      error => LoggerService.error('Error fetching packages:', error)
    );
  }

  addOrUpdatePackage(packageData: any) {
    if (packageData.id) {
      this.entityRESTService.patchEntity('packages', 'package', packageData.id, packageData).subscribe(
        () => this.fetchPackages(), // re-fetch packages to update the state
        error => LoggerService.error('Error updating package:', error)
      );
    } else {
      this.entityRESTService.createEntity('packages', 'package', packageData).subscribe(
        () => this.fetchPackages(), // re-fetch packages to update the state
        error => LoggerService.error('Error adding package:', error)
      );
    }
  }

  deletePackage(id: number): Observable<any> { // Notice the return type Observable<any>
    return this.entityRESTService.deleteEntity('packages', 'package', id).pipe(
      tap({
        next: () => this.fetchPackages(),
        error: error => {
          LoggerService.error('Error deleting package:', error);
          this.snackBar.open('Error deleting package.', 'Close', { duration: 3000 });
        }
      })
    );
  }

  manualUpdatePackages(newPackages: any[]) {
    this.packagesSubject.next(newPackages);
  }

  /**
   * The selected studio's non-retired Coaching packages, newest first, for the
   * calendar "Coaching" booking menus.
   *
   * Coaching = package lesson type "Non Unit Private" + SPS code "Non Unit",
   * the same definition the reports use (amt_dashboard_is_coaching_lesson).
   * The view's taxonomy filters match on term name. DrupalRESTService adds
   * the studio filter.
   */
  getCoachingPackages(): Observable<{ id: string, name: string }[]> {
    return this.drupalRESTService.httpGET('/api/v1/packages', [
      { parameter: 'field_lesson_type_target_id', value: 'Non Unit Private' },
      { parameter: 'field_sps_code_target_id', value: 'Non Unit' },
      { parameter: 'field_retired_value', value: '0' },
      { parameter: 'items_per_page', value: 'All' },
    ]).pipe(
      map((data: any) => (data?.['results'] || [])
        .filter((pkg: any) => pkg.field_lesson_type === 'Non Unit Private' && pkg.field_sps_code === 'Non Unit')
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .map((pkg: any) => ({ id: String(pkg.id), name: pkg.field_package_name }))),
      catchError(error => {
        LoggerService.error('Error fetching coaching packages:', error);
        return of([]);
      })
    );
  }
}
